import React, { FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../../init-store';
import I18N from '../../../../components/i18n';
import Link from '../../../../components/link';
import { Select } from '../../../../components/select';
import Spin from '../../../../components/spin';
import { supportsDeviceChange } from '../../../../helpers/browser-capabilities';
import { i18n } from '../../../../helpers/i18n';
import { ACTION, FEATURE } from '../../../../helpers/logger-enums';
import { createLogger } from '../../../../helpers/loggerFacade';
import { isMessengerIntegration } from '../../../config/selectors';
import {
    FacingMode,
    getDevicesByTypeSelector,
    getVideoFacingMode,
    getVideoInputDeviceSelector,
    selectVirtualBackgroundType,
    setVideoInputDeviceAction,
    toggleCameraFacingModeAction,
} from '../../../settings';
import { isMobileUa } from '../../../uatraits/selectors';
import { useCurrentVideoStream } from '../../hooks/useCurrentVideoStream';
import { HideParticipantsVideoSwitch } from '../hide-participants-video-switch';
import { JoinWithCameraOffSwitch } from '../join-with-camera-off-switch';
import MyVideoSwitch from '../my-video-switch';
import css from './camera.css';

const mapStateToProps = (state: RootState) => {
    return {
        supportsDeviceChange: supportsDeviceChange(),
        devices: getDevicesByTypeSelector(state, 'videoinput'),
        currentDeviceId: getVideoInputDeviceSelector(state)?.deviceId,
        facingMode: getVideoFacingMode(state),
        isMobile: isMobileUa(state),
        showJoinWithCameraOffSwitch: !isMessengerIntegration(state),
    };
};

const dispatchProps = { setVideoInputDeviceAction, toggleCameraFacingModeAction };

const connector = connect(mapStateToProps, dispatchProps);

export type CameraSectionProps = PropsWithChildren & ConnectedProps<typeof connector>;

const logger = createLogger('settings-modal-camera');

const CameraSection: FunctionComponent<CameraSectionProps> = ({
    devices,
    currentDeviceId,
    setVideoInputDeviceAction,
    supportsDeviceChange,
    isMobile,
    toggleCameraFacingModeAction,
    facingMode,
    showJoinWithCameraOffSwitch,
}) => {
    const prevVideoHeight = useRef<number>(234);

    const virtualBackgroundType = useSelector(selectVirtualBackgroundType);

    /** Используем состояние селекта на локальном стейте,
     *  т.к. через саги камера переключается не мгновенно, из-за этого в store новое значение приходит довольно долго */
    const [desiredDeviceIdOrFacingMode, setDesiredDeviceIdOrFacingMode] = useState<FacingMode | Maybe<string>>(
        isMobile ? facingMode : currentDeviceId
    );

    const { videoRef, isVideoReady, videoInputError, withMirror, setIsVideoReady } = useCurrentVideoStream({
        deviceIdOrFacingMode: desiredDeviceIdOrFacingMode,
        virtualBackgroundType,
    });

    const devicesOptions = useMemo(() => {
        if (isMobile) {
            return [
                { itemKey: FacingMode.USER, label: i18n('settings', 'camera-option-front') },
                { itemKey: FacingMode.ENVIRONMENT, label: i18n('settings', 'camera-option-back') },
            ];
        }
        return devices.map(({ deviceId, label }) => ({
            itemKey: deviceId,
            label,
        }));
    }, [devices, isMobile]);

    const isBlockedOptions = useMemo(() => {
        // Если список устройств пуст или состоит из одного элемента с пустым названием (такое может быть, когда
        // доступ дан к микрофону, но не к камере) - то блокируем опции выбора
        return devicesOptions.length <= 1 && devicesOptions.every((device) => device.label === '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devicesOptions, isVideoReady, videoInputError]);

    const setDevice = useCallback(
        (idOrModeValue: React.Key | null) => {
            const value = String(idOrModeValue);
            if (value === desiredDeviceIdOrFacingMode) {
                /** Если выбрали то же самое, то ничего не делаем, чтоб лишний раз спинер не крутить */
                return;
            }
            if (videoRef.current) {
                /** Запоминаем высоту, чтобы не прыгал лейаут */
                prevVideoHeight.current = videoRef.current.getBoundingClientRect().height;
                videoRef.current.srcObject = null;
            }

            if (isMobile) {
                toggleCameraFacingModeAction(value as FacingMode);
            } else {
                logger.startFeatureDelta({
                    feature: FEATURE.DEVICES,
                    action: ACTION.SELECT_CAMERA,
                });
                setVideoInputDeviceAction(value);
            }
            setDesiredDeviceIdOrFacingMode(value);
            setIsVideoReady(false);
        },
        // Уверены в том, что videoRef не является зависимостью
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMobile, setVideoInputDeviceAction, toggleCameraFacingModeAction, desiredDeviceIdOrFacingMode]
    );

    useEffect(() => {
        // в Safari нужно дождаться обновления списка устройств после выдачи разрешения
        if (!isMobile && currentDeviceId && currentDeviceId !== 'default') {
            setDesiredDeviceIdOrFacingMode(currentDeviceId);
        }
    }, [currentDeviceId, isMobile]);

    const isSpinVisible = !isVideoReady && !videoInputError;

    const prevHeight = !isVideoReady ? { height: `${prevVideoHeight.current}px` } : undefined;

    return (
        <>
            {supportsDeviceChange ? (
                <>
                    <Select
                        className={cn(css.select, {
                            [css.selectDisabled]: isBlockedOptions,
                        })}
                        isDisabled={isBlockedOptions}
                        items={devicesOptions}
                        selectedKey={desiredDeviceIdOrFacingMode}
                        onSelectionChange={setDevice}
                    />

                    <div className={css.cameraWrapper} style={prevHeight}>
                        <div
                            className={cn(css.spinOverlay, {
                                [css.spinVisible]: isSpinVisible,
                            })}
                        >
                            <Spin className={css.spin} size="xl" progress />
                        </div>
                        <video
                            ref={videoRef}
                            className={cn(css.CameraVideo, {
                                [css.mirror]: withMirror,
                            })}
                            autoPlay
                            playsInline
                            // remove YaBro custom buttons
                            // eslint-disable-next-line react/no-unknown-property
                            x-yandex-pip="false"
                        />
                    </div>
                </>
            ) : (
                <div className={css.UnsupportedMessage}>
                    <I18N keyset="settings" loc="unsupported-camera-change-message">
                        <Link data-ref="link-telemost" view="default" href="/download-desktop" target="_blank" />
                        <Link data-ref="link-yabro" view="default" href="https://browser.yandex.ru" target="_blank" />
                    </I18N>
                </div>
            )}
            <MyVideoSwitch />
            {showJoinWithCameraOffSwitch && <JoinWithCameraOffSwitch />}
            <HideParticipantsVideoSwitch />
        </>
    );
};

export default connector(CameraSection);
